import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Bullet } from './Bullet';
import { Explosion } from './Explosion';
import { spawnWreckage, WRECK_PALETTES } from './Wreckage';
import { useStore } from '../../state/store';
import { interceptPoint, entityVelocity } from '../aim';
import { endRunOnLastUnit, aimPointFor } from '../possession';

/**
 * Fixed gun emplacement guarding a town. A twin-MG on a pintle mount inside a sandbag
 * ring that can elevate for aircraft and depress onto ground armour. Runs on AI
 * (auto-tracks and shoots the nearest threat — air or ground) until the player jumps in,
 * then the stick slews the gun and it auto-fires when an enemy crosses the sights.
 */
const GROUND_Y = -468;
const HP = 14;
const FIRE_RANGE = 1700;
const FIRE_INTERVAL = 0.09;
const FIRE_CONE = THREE.MathUtils.degToRad(6);
const BULLET_SPEED = 720;
const SLEW_YAW = 1.8;   // rad/s — AI tracking slew
const SLEW_PITCH = 1.4;
const PITCH_MIN = -0.32; // ~ -18° (depress onto ground targets approaching the town)
const PITCH_MAX = 1.45;  // ~ 83°  (near-vertical for overhead targets)
const PLAYER_YAW_RATE = 2.2;
const PLAYER_PITCH_RATE = 1.6;

export class TownGunner extends Entity {
  camera: THREE.PerspectiveCamera | null = null;
  private yawGroup: THREE.Group;
  private pitchGroup: THREE.Group;
  private _sight?: THREE.Group; // first-person ring sight, shown only while crewed
  private yaw = 0;
  private pitch = 0.12; // start near level (looking at the horizon where planes approach)
  private fireCooldown = 0;
  private _seated = false;
  private dying = false;
  private deathTimer = 0;
  private _fwd = new THREE.Vector3();
  private _muzzle = new THREE.Vector3();
  /** Twin-MG firing: flips each shot so rounds alternate between the two barrels. */
  private _gunSide = -1;
  private _barrelMuzzle = new THREE.Vector3();
  private _toLocal = new THREE.Vector3();
  private _invMat = new THREE.Matrix4();

  /** How far above the terrain the emplacement is lifted. Trench MGs use this so the gun crest
   *  clears the ~7u earth parapets and the gunner's view isn't blocked by the trenchwork. */
  readonly elevate: number; // public so a lost gun's perch height can be restored on reinforcement

  constructor(pos: THREE.Vector3, label = 'AA GUN', elevate = 0) {
    super('ally');
    this.friendly = true;
    this.controllable = true;
    this.unitLabel = label;
    this.elevate = elevate;
    this.hp = HP;
    this.radius = 6;
    this.group.position.copy(pos);
    this.group.position.y = GROUND_Y;

    const { root, yaw: yawGroup, pitch: pitchGroup } = makeGunnerMesh(elevate);
    this.yawGroup = yawGroup;
    this.pitchGroup = pitchGroup;
    this.group.add(root);
    this.pitchGroup.rotation.x = this.pitch;

    // Sandbag emplacement OBB — squat and wide.
    this.localBounds = new THREE.Box3(
      new THREE.Vector3(-3.2, -0.5, -3.2),
      new THREE.Vector3(3.2, 4.2, 3.2),
    );
  }

  alive(): boolean { return !this.dying; }
  hpFrac(): number { return THREE.MathUtils.clamp(this.hp / HP, 0, 1); }

  possess(camera: THREE.PerspectiveCamera): void {
    this.controlled = true;
    this.camera = camera;
    // Gunner's eye: mounted on the PITCH cradle, set above and behind the breech looking
    // out OVER the gun and down the twin barrels, so the screen-centre gunsight crosshair
    // marks the line of fire. The view elevates with the gun (AA-style) — at extreme
    // elevation you're looking up, exactly right for tracking aircraft overhead.
    // Sighted from just AHEAD of the pan magazine, at barrel height, looking straight down
    // the twin barrels — the receiver/magazine sit behind the camera so they don't fill the
    // view; only the barrels (with cooling jackets) recede toward the crosshair.
    this.pitchGroup.add(camera);
    // Raised higher above the breech (but still looking straight -Z so screen-centre = line of
    // fire = the HUD crosshair — a downward tilt would misalign them). The higher eye drops the
    // twin barrels/jackets into the lower frame instead of filling the centre.
    camera.position.set(0, 0.5, -0.05);
    camera.rotation.set(0, 0, 0);
    camera.fov = 62;
    camera.updateProjectionMatrix();
    // First-person framing: a brass ring-and-bead AA sight on a post ahead of the eye, plus the
    // breech/receiver just below — so even when the gun is elevated at empty sky the view reads as
    // sighting down a real weapon. The ring encircles the HUD crosshair (line of fire) without
    // blocking it; the receiver sits in the lower frame.
    if (!this._sight) { this._sight = makeGunnerSight(); this.pitchGroup.add(this._sight); }
    this._sight.visible = true;
    useStore.getState().setHp(Math.max(0, Math.ceil(this.hp)), HP);
  }

  release(): void {
    this.controlled = false;
    if (this._sight) this._sight.visible = false; // hide first-person sight when not crewed
    if (this.camera && this.camera.parent === this.pitchGroup) this.pitchGroup.remove(this.camera);
    this.camera = null;
  }

  damage(amount: number, ctx: EntityCtx): void {
    if (this.dying) return;
    this.hp -= amount;
    if (this.controlled) {
      useStore.getState().takeDamage();
      useStore.getState().setHp(Math.max(0, Math.floor(this.hp)), HP);
    }
    if (this.hp <= 0) this.onDestroyed(ctx);
  }

  private onDestroyed(ctx: EntityCtx) {
    if (this.controlled) {
      const handed = ctx.game.onControlledLost(this);
      if (handed) this.release();
      else endRunOnLastUnit(this, ctx);
    }
    this.dying = true;
    // Blow the emplacement apart — barrels, sandbags, shield and mount tumble away.
    spawnWreckage(ctx, this.group.position.clone().add(new THREE.Vector3(0, 1.2, 0)), 'gun', WRECK_PALETTES.gun,
      { count: 7, boom: 1.0 });
    this.dead = true;
  }

  update(ctx: EntityCtx): void {
    const { game, dt } = ctx;
    if (this.dying) {
      this.deathTimer += dt;
      if (this.deathTimer < 0.5 && Math.random() < 0.4) {
        const off = new THREE.Vector3((Math.random() - 0.5) * 5, 2, (Math.random() - 0.5) * 5);
        new Explosion(this.group.position.clone().add(off), 12, 0.5, 'fire').addTo(ctx);
      }
      // Don't sweep the wreck if it's the unit the game-over camera is parked on — that
      // would dispose the group the loop still reads. Only swept-away during live play.
      if (this.deathTimer > 4 && useStore.getState().phase === 'playing') this.dead = true;
      return;
    }
    const phase = useStore.getState().phase;
    if (phase === 'gameover') return;

    // Seat the emplacement on the terrain surface (done once — it never moves). Trench MGs lift
    // by `elevate` so the gun + gunner's eye clear the earth parapets flanking the trench.
    if (!this._seated) {
      this.group.position.y = game.gameScene.heightAt(this.group.position.x, this.group.position.z) + this.elevate;
      this._seated = true;
    }

    // Acquire the nearest live threat within range — aircraft OR ground armour — then
    // resolve the world point to actually shoot at: the nearest live hit-part for
    // multi-part bosses (their centroid is invincible), led for bullet travel time.
    const target = this.acquireTarget(game);
    this.engageTargetId = target ? target.id : 0; // for the map's "engaging" line
    let aimWorld: THREE.Vector3 | null = null;
    if (target) {
      const muzzle = this.muzzleWorld().clone();
      const base = aimPointFor(target, muzzle, new THREE.Vector3());
      aimWorld = interceptPoint(muzzle, base, entityVelocity(target), BULLET_SPEED);
    }

    const sgun = useStore.getState();
    const aiDriven = !this.controlled || sgun.ai || sgun.mapOpen;
    if (aiDriven) {
      if (aimWorld) this.aimToward(aimWorld, SLEW_YAW * dt, SLEW_PITCH * dt);
    } else {
      // Player aims with the stick: x = yaw; pull back = raise the barrel, push forward =
      // depress it (same yoke convention as flying — getStick already orients the gamepad
      // so -y is "back", matching the plane's pull-back-to-climb).
      const stick = game.input.getStick();
      this.yaw -= stick.x * PLAYER_YAW_RATE * dt;
      this.pitch -= stick.y * PLAYER_PITCH_RATE * dt;
      this.pitch = THREE.MathUtils.clamp(this.pitch, PITCH_MIN, PITCH_MAX);
      this.yawGroup.rotation.y = this.yaw;
      this.pitchGroup.rotation.x = this.pitch;
    }

    // Manual fire when crewed (RT / touch FIRE); AI crews auto-fire when the lead point is in cone.
    this.fireCooldown -= dt;
    const autoOnTarget = !!aimWorld && this.aimInCone(aimWorld);
    const firing = this.controlled ? game.fireHeld() : autoOnTarget;
    if (firing && this.fireCooldown <= 0) {
      // Crewed: rounds follow the barrel (where you've aimed); AI: the computed lead point.
      const aim = this.controlled
        ? this.muzzleWorld().clone().add(this.barrelForward().clone().multiplyScalar(FIRE_RANGE))
        : aimWorld!;
      this.shootToward(ctx, aim);
      // ACE BONUS: a manned gun fires faster than its AI crew (AI baseline unchanged).
      this.fireCooldown = this.controlled ? FIRE_INTERVAL * 0.6 : FIRE_INTERVAL;
    }
    if (this.controlled) useStore.getState().setFiring(firing);
  }

  private acquireTarget(game: EntityCtx['game']): Entity | null {
    const muzzle = this.muzzleWorld();
    let best: Entity | null = null;
    let bestD = FIRE_RANGE * FIRE_RANGE;
    // Engages aircraft AND ground armour now — a town gun defends against anything that
    // comes within range. Picks the nearest live threat of either kind.
    for (const e of game.findEnemies()) {
      if (!e.alive()) continue;
      if (e.trainingTarget && !this.controlled) continue;
      const d = e.position.distanceToSquared(muzzle);
      if (d < bestD) { bestD = d; best = e; }
    }
    return best;
  }

  private muzzleWorld(): THREE.Vector3 {
    this.pitchGroup.updateWorldMatrix(true, false);
    return this._muzzle.set(0, 0, -2.4).applyMatrix4(this.pitchGroup.matrixWorld);
  }

  private barrelForward(): THREE.Vector3 {
    return this._fwd.set(0, 0, -1).applyQuaternion(this.pitchGroup.getWorldQuaternion(_tmpQ)).normalize();
  }

  /** Slew current yaw/pitch toward a world aim point, capped by per-frame step. */
  private aimToward(worldAim: THREE.Vector3, maxYawStep: number, maxPitchStep: number) {
    this.group.updateWorldMatrix(true, false);
    this._invMat.copy(this.group.matrixWorld).invert();
    this._toLocal.copy(worldAim).applyMatrix4(this._invMat); // gunner-local offset
    const desiredYaw = Math.atan2(-this._toLocal.x, -this._toLocal.z);
    const horiz = Math.hypot(this._toLocal.x, this._toLocal.z);
    const desiredPitch = THREE.MathUtils.clamp(Math.atan2(this._toLocal.y, horiz), PITCH_MIN, PITCH_MAX);
    // Shortest-arc yaw step.
    let dYaw = desiredYaw - this.yaw;
    dYaw = Math.atan2(Math.sin(dYaw), Math.cos(dYaw));
    this.yaw += THREE.MathUtils.clamp(dYaw, -maxYawStep, maxYawStep);
    const dPitch = desiredPitch - this.pitch;
    this.pitch += THREE.MathUtils.clamp(dPitch, -maxPitchStep, maxPitchStep);
    this.yawGroup.rotation.y = this.yaw;
    this.pitchGroup.rotation.x = this.pitch;
  }

  private aimInCone(aimWorld: THREE.Vector3): boolean {
    const muzzle = this.muzzleWorld();
    const to = aimWorld.clone().sub(muzzle);
    const d = to.length();
    if (d > FIRE_RANGE || d < 1e-3) return false;
    to.multiplyScalar(1 / d);
    return to.dot(this.barrelForward()) > Math.cos(FIRE_CONE);
  }

  private shootToward(ctx: EntityCtx, aimWorld: THREE.Vector3) {
    // Twin barrels: same muzzle distance as before (-2.4), just offset left/right so rounds
    // alternate between the two barrels and line up with them.
    this._gunSide = -this._gunSide;
    this.pitchGroup.updateWorldMatrix(true, false);
    const muzzle = this._barrelMuzzle.set(this._gunSide * 0.18, 0.05, -2.4)
      .applyMatrix4(this.pitchGroup.matrixWorld).clone();
    const dir = aimWorld.clone().sub(muzzle).normalize();
    const b = new Bullet(muzzle.clone(), dir.multiplyScalar(BULLET_SPEED), this.id, 'player');
    if (this.controlled) b.damageAmount = 2; // ACE BONUS: harder-hitting rounds when manned
    b.friendlyFire = this.controlled; // your own fire can hit friendlies + villages
    ctx.game.add(b);
    if (Math.random() < 0.5) new Explosion(muzzle.clone(), 3, 0.1, 'fire').addTo(ctx);
  }
}

const _tmpQ = new THREE.Quaternion();

/** Lead a moving target for a bullet of the given speed (linear intercept). `base` is the
 *  world point being aimed at (a specific hit-part for bosses); velocity is the target's. */

function makeGunnerMesh(elevate = 0): { root: THREE.Group; yaw: THREE.Group; pitch: THREE.Group } {
  const root = new THREE.Group();
  const sandbag = new THREE.MeshStandardMaterial({ color: 0x9c8a5a, roughness: 0.98, flatShading: true });
  const sandbagDark = new THREE.MeshStandardMaterial({ color: 0x7a6a44, roughness: 0.98, flatShading: true });
  const sandbagPale = new THREE.MeshStandardMaterial({ color: 0xb0a06e, roughness: 0.98, flatShading: true });
  const metal = new THREE.MeshStandardMaterial({ color: 0x2c2c2e, roughness: 0.4, metalness: 0.7 });
  const blackMetal = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.55, metalness: 0.5 });
  const olive = new THREE.MeshStandardMaterial({ color: 0x4b5320, roughness: 0.8 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb08a3a, roughness: 0.5, metalness: 0.6 });
  const timber = new THREE.MeshStandardMaterial({ color: 0x4a3826, roughness: 0.95, flatShading: true });

  // Elevated trench firing platform: when lifted, the whole emplacement is raised by `elevate` so
  // the gun clears the parapet, and we build a stout earth/timber dugout column DOWN to ground
  // level (local y = -elevate) so the position reads as a raised bunker, not a floating gun.
  if (elevate > 0) {
    // Earth column the platform stands on.
    const colH = elevate + 0.5;
    const pillar = new THREE.Mesh(new THREE.CylinderGeometry(3.0, 3.4, colH, 16, 1), sandbagDark);
    pillar.position.y = -elevate + colH / 2 - 0.5;
    root.add(pillar);
    // Timber deck the gun + sandbags sit on, at platform level.
    const deck = new THREE.Mesh(new THREE.CylinderGeometry(3.3, 3.3, 0.4, 20, 1), timber);
    deck.position.y = -0.2;
    root.add(deck);
    // Plank revetment hoops banding the earth column.
    for (let b = 0; b < 3; b++) {
      const hoop = new THREE.Mesh(new THREE.TorusGeometry(3.15 - b * 0.05, 0.12, 6, 20), timber);
      hoop.rotation.x = Math.PI / 2;
      hoop.position.y = -0.6 - b * (colH / 3);
      root.add(hoop);
    }
  }

  // --- Sandbag emplacement: three stacked, staggered rings of plump bags, each rounded
  //     (segmented box + jitter) so the wall reads as packed sacks, not crates. ---
  const bagMats = [sandbag, sandbagDark, sandbagPale];
  for (let ring = 0; ring < 3; ring++) {
    const count = 20;
    const r = 3.0 - ring * 0.12;
    const y = 0.45 + ring * 0.62;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + (ring % 2 ? Math.PI / count : 0);
      const bag = new THREE.Mesh(
        new THREE.BoxGeometry(1.15, 0.6, 0.85, 4, 4, 4),
        bagMats[(i + ring) % 3],
      );
      // Round the bag corners by squashing the segmented box a touch via scale.
      bag.scale.set(1, 0.92 + ((i * 7) % 5) * 0.03, 1);
      bag.position.set(
        Math.cos(a) * r + (Math.sin(i * 3.1) * 0.06),
        y + (Math.cos(i * 2.3) * 0.04),
        Math.sin(a) * r,
      );
      bag.rotation.y = -a + (Math.sin(i) * 0.08);
      root.add(bag);
    }
  }
  // Ammo crates stacked inside the pit.
  for (let i = 0; i < 3; i++) {
    const crate = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.4, 0.5, 3, 2, 2), olive);
    crate.position.set(-2.0, 0.45 + (i % 2) * 0.42, 1.8 - i * 0.55);
    crate.rotation.y = 0.3 * i;
    root.add(crate);
  }

  // --- Pedestal: pintle mount on a tripod of splayed legs bolted to a base plate. ---
  const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.16, 48, 1), metal);
  plate.position.y = 0.08;
  root.add(plate);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 1.1, 24, 2), metal);
    leg.position.set(Math.cos(a) * 0.55, 0.55, Math.sin(a) * 0.55);
    leg.rotation.z = -Math.cos(a) * 0.45;
    leg.rotation.x = Math.sin(a) * 0.45;
    root.add(leg);
  }
  const column = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.6, 1.05, 48, 3), metal);
  column.position.y = 0.62;
  root.add(column);
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.18, 48, 1), blackMetal);
  collar.position.y = 1.05;
  root.add(collar);

  // Yaw group rotates about Y (the traverse).
  const yaw = new THREE.Group();
  yaw.position.y = 1.12;
  root.add(yaw);
  // Trunnion fork the cradle pivots in.
  for (const dx of [-0.34, 0.34]) {
    const cheek = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.5, 0.34, 2, 4, 3), metal);
    cheek.position.set(dx, 0.25, 0.1);
    yaw.add(cheek);
  }
  // Pitch group rotates about X (the cradle that elevates).
  const pitch = new THREE.Group();
  pitch.position.y = 0.32;
  yaw.add(pitch);

  // Gun receiver body.
  const receiver = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 1.3, 4, 4, 8), blackMetal);
  receiver.position.set(0, 0, 0.25);
  pitch.add(receiver);
  // Curved gunner's shield plate at the front of the receiver — kept low so it frames the
  // bottom of the gunner's view rather than blocking it.
  const shield = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.7, 40, 2, true, -0.7, 1.4), metal);
  shield.rotation.y = Math.PI / 2;
  shield.position.set(0, 0.05, -0.35);
  shield.scale.set(0.5, 1, 1);
  pitch.add(shield);

  // Twin barrels with perforated cooling jackets + fins (point along -Z).
  for (const dx of [-0.18, 0.18]) {
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 2.4, 40, 3), metal);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(dx, 0.05, -1.1);
    pitch.add(barrel);
    // Ventilated jacket — a slotted sleeve over the barrel root.
    const jacket = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 1.0, 40, 3), metal);
    jacket.rotation.x = Math.PI / 2;
    jacket.position.set(dx, 0.05, -0.55);
    pitch.add(jacket);
    // Cooling fins.
    for (let f = 0; f < 6; f++) {
      const fin = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.03, 32, 1), metal);
      fin.rotation.x = Math.PI / 2;
      fin.position.set(dx, 0.05, -0.95 + f * 0.16);
      pitch.add(fin);
    }
    // Muzzle cap.
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.12, 28, 1), blackMetal);
    cap.rotation.x = Math.PI / 2;
    cap.position.set(dx, 0.05, -2.32);
    pitch.add(cap);
  }
  // Pan magazine sitting on top of the receiver (brass-rimmed drum).
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.16, 48, 2), brass);
  drum.position.set(0, 0.42, 0.05);
  pitch.add(drum);
  const drumLip = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.04, 16, 48), metal);
  drumLip.rotation.x = Math.PI / 2;
  drumLip.position.set(0, 0.5, 0.05);
  pitch.add(drumLip);

  // Rear spade grips + ring/post sights.
  const gripBar = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.1, 0.12, 4, 1, 1), blackMetal);
  gripBar.position.set(0, 0.02, 0.92);
  pitch.add(gripBar);
  for (const dx of [-0.22, 0.22]) {
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.34, 24, 2), blackMetal);
    grip.position.set(dx, -0.16, 0.95);
    pitch.add(grip);
  }
  const rearSight = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.018, 16, 40), metal);
  rearSight.position.set(0, 0.34, 0.2);
  pitch.add(rearSight);
  const frontPost = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.18, 12, 1), metal);
  frontPost.position.set(0, 0.32, -1.9);
  pitch.add(frontPost);

  root.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return { root, yaw, pitch };
}

/** First-person AA ring sight for the crewed gun view. Built in pitch-cradle local space; the
 *  camera sits at ~(0, 0.2, -0.15) looking -Z down the barrels. A brass foresight ring on a post
 *  sits ahead of the eye (encircling the centre line of fire), with a small rear bead to sight
 *  through and the receiver top just in the lower frame. Centre stays clear for the HUD crosshair. */
function makeGunnerSight(): THREE.Group {
  const g = new THREE.Group();
  const brass = new THREE.MeshStandardMaterial({ color: 0xb08a3a, roughness: 0.45, metalness: 0.7 });
  const blackMetal = new THREE.MeshStandardMaterial({ color: 0x141414, roughness: 0.55, metalness: 0.5 });

  // Foresight ring on a post, ahead of the eye — the AA "cartwheel" sight. Sits LOW in the frame
  // (below the raised eye line at y0.5) so it doesn't ride the centre; the weapon reads bottom-up.
  const ringY = 0.16;
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.15, 0.012, 10, 28), brass);
  ring.position.set(0, ringY, -1.15);
  g.add(ring);
  // Cross spider in the ring (thin, doesn't obscure).
  for (const rot of [0, Math.PI / 2]) {
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.006, 0.006), brass);
    spoke.position.copy(ring.position); spoke.rotation.z = rot;
    g.add(spoke);
  }
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.4, 8), blackMetal);
  post.position.set(0, ringY - 0.26, -1.15);
  g.add(post);
  // Rear bead — a small ball just in front of the eye to line up with the ring.
  const bead = new THREE.Mesh(new THREE.SphereGeometry(0.016, 8, 6), brass);
  bead.position.set(0, ringY, -0.5);
  g.add(bead);
  // Receiver + magazine sitting along the bottom of the frame so the weapon body is present.
  const recv = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.2, 0.7, 2, 1, 2), blackMetal);
  recv.position.set(0, -0.1, -0.1);
  g.add(recv);
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.1, 28), brass);
  drum.position.set(0, 0.04, 0.05);
  g.add(drum);

  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}
